import { useState, useEffect, memo } from 'react';
import { Link } from 'react-router-dom';
import { Server, Users, Globe, Wifi, WifiOff, ExternalLink } from 'lucide-react';
import { serverListApiService, ServerListEntry } from '../../services/serverListApi';
import { SkeletonLoader } from '../SkeletonLoader';
import { SectionHeader } from '../ui/SectionHeader';
import { cn } from '../../utils/cn';

interface PackServerInfoProps {
  mpiId: number;
  className?: string;
}

export const PackServerInfo = memo(({ mpiId, className }: PackServerInfoProps) => {
  const [servers, setServers] = useState<ServerListEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    serverListApiService.getServersByMpiId(mpiId)
      .then(setServers)
      .catch(() => setServers([]))
      .finally(() => setLoading(false));
  }, [mpiId]);

  if (loading) {
    return (
      <div className={cn('glass-card p-4 md:p-5', className)} aria-busy="true">
        <SkeletonLoader className="h-4 w-48 mb-3" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <SkeletonLoader className="h-16 w-full rounded-lg" />
          <SkeletonLoader className="h-16 w-full rounded-lg" />
        </div>
      </div>
    );
  }

  if (servers.length === 0) return null;

  const totalOnline = servers.reduce((sum, s) => sum + s.clusterOnlinePlayerCount, 0);
  const totalMax = servers.reduce((sum, s) => {
    // Sum max across all cluster children
    const childMax = (s.clusterChildren || []).reduce((cs, c) => cs + c.maxPlayerCount, 0);
    return sum + (childMax || s.maxPlayerCount);
  }, 0);

  return (
    <div className={cn('glass-card p-4 md:p-5', className)}>
      <SectionHeader
        title="CDU Servers Running This Pack"
        icon={Server}
        iconClassName="text-green-400"
        count={servers.length}
        actions={
          <span className="inline-flex items-center gap-1.5 text-xs text-glass-400 tabular-nums">
            <Users className="w-3.5 h-3.5" />
            <span>{totalOnline}/{totalMax} players</span>
          </span>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {servers.map((server) => (
          <div
            key={server.serverId}
            className="subcard p-3 space-y-2"
          >
            {/* Server header */}
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                {server.isServerOnline ? (
                  <Wifi className="w-3.5 h-3.5 text-green-400 shrink-0" />
                ) : (
                  <WifiOff className="w-3.5 h-3.5 text-red-400 shrink-0" />
                )}
                <span className="text-sm font-semibold text-white truncate">{server.clusterName}</span>
                {server.modpackVersion && (
                  <span className="text-xs font-mono text-glass-400">v{server.modpackVersion}</span>
                )}
              </div>
              <Link
                to={`/status-monitor`}
                className="btn-icon btn-icon-sm"
                title="Open status monitor"
                aria-label="Open status monitor"
              >
                <ExternalLink className="w-3 h-3" />
              </Link>
            </div>

            {/* Cluster children (individual servers) */}
            {server.clusterChildren && server.clusterChildren.length > 0 && (
              <div className="space-y-1">
                {server.clusterChildren.map((child) => (
                  <div
                    key={child.serverAddress}
                    className="flex items-center justify-between gap-2 text-xs"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className={cn(
                        'w-1.5 h-1.5 rounded-full shrink-0',
                        child.isServerOnline ? 'bg-green-400' : 'bg-red-400',
                      )} />
                      <span className="font-mono text-glass-300 truncate">{child.serverAddress}</span>
                      <span className="flex items-center gap-0.5 text-glass-500 shrink-0">
                        <Globe className="w-3 h-3" />
                        <span>{child.regionName}</span>
                      </span>
                    </div>
                    <span className="text-glass-400 tabular-nums shrink-0">
                      {child.onlinePlayerCount}/{child.maxPlayerCount}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Metadata */}
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-glass-500">
              {server.modpackLauncher && (
                <span>{server.modpackLauncher}</span>
              )}
              {server.minecraftVersion && (
                <span>MC {server.minecraftVersion}</span>
              )}
              {server.modpackAuthor && (
                <span>by {server.modpackAuthor}</span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
});

PackServerInfo.displayName = 'PackServerInfo';
